import styled from '@xstyled/styled-components';
import React from 'react';
import { useHistory } from 'react-router';
import { animated } from '@react-spring/web';
import { t } from 'ttag';
import ArrowIcon from '~/assets/icons/arrow_for_tooltip.svg';
import { useFade } from '~/lib/transitions';
import { useLightSwitchesAudioEffect } from '~/ui/audio-effect/useLightSwitchesAudioEffect';
import { BurgerButtonEds } from '~/ui/buttons/BurgerButtonEds';
import { HelpButtonEds } from '~/ui/buttons/HelpButtonEds';
import { NextButtonEds } from '~/ui/buttons/NextButtonEds';
import { RewindButtonEds } from '~/ui/buttons/RewindButtonEds';
import { SettingsButtonEds } from '~/ui/buttons/SettingsButtonEds';
import { useTrainingStore } from '../state';
import { useTrainingUrl } from '../useTrainingUrl';
import { OnboardingStages, useOnboardingStore } from './Store';
import { ChatGptButton } from '~/ui/buttons';

const BurgerBtn = styled(BurgerButtonEds)`
  position: fixed;
  top: 60px;
  left: 20px;
  z-index: hud;
`;

const BurgerDescription = styled.divBox`
  position: fixed;
  top: 110px;
  left: 60px;
  z-index: hud;
  color: #ffffff;
  font-size: 16px;
  font-family: Raleway;
  > svg {
    margin-right: 15px;
    margin-bottom: 5px;
  }
  @media (max-width: sm) {
    font-size: 12px;
    width: 160px;
    > svg {
      display: none;
    }
  }
`;

const SettingBtn = styled(SettingsButtonEds)`
  position: fixed;
  top: 20px;
  right: 8px;
  font-size: 30px;
  z-index: hud;
`;

const SettingDescription = styled.divBox`
  position: fixed;
  top: 80px;
  right: 140px;
  z-index: hud;
  color: #ffffff;
  font-size: 16px;
  font-family: Raleway;
  > svg {
    position: absolute;
    top: -30px;
    right: -80px;
    transform: matrix(-0.94, -0.35, -0.35, 0.94, 0, 0);
  }
  @media (max-width: sm) {
    top: 25px;
    right: 70px;
    font-size: 12px;
    > svg {
      display: none;
    }
  }
`;

const HelpBtn = styled(HelpButtonEds)`
  position: fixed;
  top: 70px;
  right: 8px;
  font-size: 30px;
  z-index: hud;
`;

const ChatBtn = styled(ChatGptButton)`
  position: fixed;
  top: 170px;
  right: 8px;
  font-size: 30px;
  z-index: hud;
  padding: 0px;
  z-index: hud;
  @media ((max-width: 580px) and (max-height: 300px)) {
    right: 10px;
  }
`;

const HelpDescription = styled.divBox`
  position: fixed;
  top: 150px;
  right: 120px;
  z-index: hud;
  color: #ffffff;
  font-size: 16px;
  font-family: Raleway;
  > svg {
    position: absolute;
    right: -70px;
    top: -45px;
    transform: rotate(10deg) scaleX(-1);
  }
  @media (max-width: sm) {
    font-size: 12px;
    top: 75px;
    right: 70px;
    > svg {
      display: none;
    }
  }
`;

const ChatDescription = styled.divBox`
  position: fixed;
  top: 250px;
  right: 120px;
  z-index: hud;
  color: #ffffff;
  font-size: 16px;
  font-family: Raleway;
  > svg {
    position: absolute;
    right: -70px;
    top: -45px;
    transform: rotate(10deg) scaleX(-1);
  }
  @media (max-width: sm) {
    font-size: 12px;
    top: 177px;
    right: 65px;
    > svg {
      display: none;
    }
  }
`;

const RewindBtn = styled(RewindButtonEds)`
  position: fixed;
  bottom: 8px;
  left: 8px;
  z-index: 100;
`;

const RewindDescription = styled.divBox`
  position: fixed;
  bottom: 90px;
  left: 70px;
  z-index: hud;
  color: #ffffff;
  font-size: 16px;
  font-family: Raleway;
  > svg {
    margin-bottom: -75px;
    transform: rotate(-75deg);
  }
  @media (max-width: sm) {
    bottom: 60px;
    left: 50px;
    font-size: 12px;
    > svg {
      display: none;
    }
  }
`;

const ContinueDescription = styled.divBox`
  position: fixed;
  bottom: 110px;
  right: 170px;
  z-index: hud;
  color: #ffffff;
  font-size: 16px;
  font-family: Raleway;
  > svg {
    position: absolute;
    right: 0px;
    transform: matrix(0, -1, -1, 0, 0, 0);
  }
  @media (max-width: sm) {
    right: 100px;
    bottom: 130px;
  }
`;

const Continue = styled(NextButtonEds)`
  position: fixed;
  right: 8px;
  bottom: 8px;
  z-index: hud;
`;

export const Outro = () => {
  const stage = useOnboardingStore(s => s.stage);
  const next = useTrainingStore(s => s.next);
  const featureShowChatbot = useTrainingStore(s => s.featureShowChatbot);
  const url = useTrainingUrl();
  const history = useHistory();
  const transition = useFade(stage === OnboardingStages.Outro, 'opacity', 0, 1);
  const [playAudioEffect] = useLightSwitchesAudioEffect();

  const start = async () => {
    try {
      playAudioEffect();
    } catch (error) {
      // Silently ignore audio effect errors
    }
    const id = await next();
    history.push(`${url}/${id}`);
  };

  return (
    <>
      {transition(
        (style, item) =>
          item && (
            <animated.div style={style}>
              <div>
                <BurgerBtn disabled />
                <BurgerDescription>
                  <ArrowIcon />
                  {t`Navigation & chapter selection`}
                </BurgerDescription>
              </div>
              <div>
                <SettingBtn disabled />
                <SettingDescription>
                  {t`Settings & subtitles`}
                  <ArrowIcon />
                </SettingDescription>
              </div>
              <div>
                <HelpBtn disabled />
                <HelpDescription>
                  {t`Help or send Feedback`}
                  <ArrowIcon />
                </HelpDescription>
              </div>
              {featureShowChatbot && (
                <div>
                  <ChatBtn disabled />
                  <ChatDescription>
                    {t`Ask questions to your A.I. chatbot`}
                    <ArrowIcon />
                  </ChatDescription>
                </div>
              )}
              <div>
                <RewindBtn disabled />
                <RewindDescription>
                  <ArrowIcon />
                  {t`Go back 10 seconds`}
                </RewindDescription>
              </div>
              <div>
                <ContinueDescription>
                  {t`Press to continue`}
                  <ArrowIcon />
                </ContinueDescription>
              </div>
            </animated.div>
          )
      )}
      {stage === OnboardingStages.Outro && (
        <Continue onClick={start} data-test-id="outro-overlay-next-button" />
      )}
    </>
  );
};
